import { Control } from '../Control';
import withField from '../withField';
import { withHandlers } from 'recompose';
import DropDownIcon from '../Icons/DropDownIcon';
import React, { Component, Fragment } from 'react';
import { compose, invertObj } from 'ramda';
import ValidationIcon from '../Icons/ValidationIcon';
import buildClassName from 'js/utils/classNameBuilder.util';
import { withTranslations } from 'js/components/hocs/withTranslations';

import { EDITABLE_DD_MODES } from './constants';

import style from './index.scss';

export class EditableDropDown extends Component {
    constructor(props) {
        super(props);
        this.selectRef = React.createRef();
    }

    componentDidMount() {
        const { input: { value: { select } } } = this.props;

        if (this.selectRef.current) {
            this.selectRef.current.value = select;
        }
    }

    static defaultProps = {
        name: '',
        label: '',
        hint: '',
        placeholder: '',
        disabled: false,
        currencySymbol: '',
        mode: EDITABLE_DD_MODES.ALL,
        displayValuesMap: {},
        dataList: [],
        className: '',
        classNames: {}
    };

    cache = {};

    get preparedDataList() {
        const { dataList, displayValuesMap } = this.props;

        if (dataList !== this.cache.dataList || displayValuesMap !== this.cache.displayValuesMap) {
            this.cache = {
                ...this.cache,
                dataList,
                displayValuesMap,
                preparedDataList: dataList.map(
                    item =>
                        typeof item !== 'object'
                            ? {
                                displayValue: this.getDisplayValue(item),
                                value: item
                            }
                            : item)
            };
        }

        return this.cache.preparedDataList;
    }

    getDisplayValue(value) {
        const { displayValuesMap } = this.props;

        return value in displayValuesMap ? displayValuesMap[value] : value;
    }

    render() {
        const {
            /* rff render props */
            input,
            meta,

            /* user provided props */
            id,
            name,
            label,
            t,
            hint,
            mode,
            placeholder,
            disabled,
            currencySymbol,
            dataList,
            displayValuesMap,
            className,
            classNames = {},

            ...inputsDomProps
        } = this.props;
        const { value, onFocus, onChange, onBlur } = input;

        const onChangeHandler = (event) => {
            const { target: { value: val } } = event;

            if (this.selectRef.current) {
                this.selectRef.current.value = val;
            }

            onChange(event);
        };

        const { active, error, touched, submitting } = meta;
        const { LIST_ONLY, TEXT_ONLY } = EDITABLE_DD_MODES;

        const dataCurrencyAttribute = currencySymbol
            ? { 'data-currency-symbol': currencySymbol }
            : {};

        const enableInputText = mode !== LIST_ONLY;

        let rootClassName = className;

        if (!enableInputText) {
            rootClassName += ` ${buildClassName(['readOnly'], style, classNames)}`;
        }

        const controlClassName = buildClassName(['control'], style, classNames);
        const validIconClassName = buildClassName(['validationIcon'], style, classNames);

        return (
            <Control {...{
                id,
                meta,
                className: `${rootClassName} ${controlClassName} cms-editable-drop-down`,
                classNames,
                label,
                hint,
                ...dataCurrencyAttribute,
                animate: true
            }}>
                <input
                    {...inputsDomProps}
                    id={id}
                    type="text"
                    autoComplete="off"
                    name={name}
                    value={value.input}
                    placeholder={placeholder}
                    onChange={onChangeHandler}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    disabled={disabled}
                    readOnly={!enableInputText}
                    {...submitting ? { disabled: true } : {}}
                />

                {touched && !active && <ValidationIcon
                    animate={true}
                    isValid={!error}
                    aria-label={error}
                    tabIndex={error ? 0 : -1}
                    className={`${validIconClassName} cms-editable-drop-down-icon`}
                />}

                {mode !== TEXT_ONLY && (
                    <Fragment>
                        <select
                            {...inputsDomProps}
                            id={id}
                            name={name}
                            ref={this.selectRef}
                            onChange={onChange}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            disabled={disabled}
                            {...submitting ? { disabled: true } : {}}
                        >
                            {this.preparedDataList.map(({ value: v, displayValue }, index) => (
                                <option key={`edd-${index}`} value={v}>
                                    {displayValue}
                                </option>
                            ))}
                        </select>
                        <DropDownIcon
                            className="cms-editable-drop-down-icon"
                            classNames={classNames}
                            disabled={disabled}
                            separator={true}
                        />
                    </Fragment>
                )}
            </Control>
        );
    }
}

export default compose(
    withHandlers({
        format: ({ displayValuesMap }) => (value) => {
            const input = value in displayValuesMap ? displayValuesMap[value] : value;

            return { input, select: value };
        },
        parse: ({ displayValuesMap }) => (value) => {
            const displayValue = value.trim();
            const displayValuesMapInverted = invertObj(displayValuesMap);

            return displayValue in displayValuesMapInverted
                ? displayValuesMapInverted[displayValue]
                : displayValue;
        }
    }),
    withTranslations,
    withField
)(EditableDropDown);
